import { getPlatform } from 'src/utils/platform';
import { env } from 'src/config/env';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import axios from 'axios';
import { isTestFlight } from 'src/modules/ios';
import { User } from 'src/types/user.types';
import { getBackendUrl } from 'src/config/backend';
import { errorIdentity } from 'src/modules/logs/errorIdentity';
import { buildErrorBoundaryContext } from 'src/modules/logs/errorBoundaryContext';
import { getOrCreateInstallId, peekInstallId } from 'src/modules/logs/installId';
import { getAppVersion } from 'src/modules/appVersion';
import { classifyCapgoLine, type CapgoLineVerdict } from 'src/modules/logs/capgoConsoleRules';
import { recordCapgoConsoleLine } from 'src/modules/boot/capgoConsoleRing';
import { stripUrlQueries } from 'src/modules/boot/capgoReason';
import { queryKeys } from 'src/queries/keys';

const CONSOLE_SENTRY_LOG = '[Sentry] Error:';
// Capgo's native plugin bridges its logs into the JS console, and captureConsoleIntegration turns
// every console.error into a Sentry issue; this 0ms line is a vendor defect that logs on every launch
// though the update succeeds. Exact match only: never widen to the shared stem (the 10000ms line differs).
const BENIGN_VENDOR_CONSOLE_ERRORS = [
  '🔴 ✨  CapgoUpdater : Semaphore wait timed out after 0ms',
];
// The benign 0ms line and the 10000ms resume-path-noise line (see the fork below) share one Sentry
// grouping hash, so the acceptance signal for the plugin bump would stay buried under the benign issue.
// The exact-match drop fails open, so the suffix guard below is what makes the fork safe: keep it.
const CAPGO_SEMAPHORE_STEM = 'CapgoUpdater : Semaphore wait timed out after ';
// The fork must never claim a `0ms` line, or a resurfaced benign line is mislabelled as resume noise.
const BENIGN_SEMAPHORE_SUFFIX = 'after 0ms';
export interface ReportErrorContext {
  fingerprint?: string[];
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

const stripIfString = (value: unknown): unknown =>
  typeof value === 'string' ? stripUrlQueries(value) : value;

interface QueryStrippableEvent {
  message?: string;
  extra?: Record<string, unknown>;
  breadcrumbs?: unknown;
}

const URL_KEYS = ['url', 'http.url'];

// Steps are independent: a throw in one (frozen or exotic input) must never skip another.
const bestEffort = (step: () => void): void => {
  try {
    step();
  } catch {
    // scrubbing must never lose the event
  }
};

const scrubCrumbMessage = (crumb: unknown): unknown => {
  try {
    const message: unknown = (crumb as { message?: unknown } | null)?.message;
    if (typeof message !== 'string') return crumb;
    return { ...(crumb as object), message: stripUrlQueries(message) };
  } catch {
    // Fail closed: an unreadable message is dropped rather than sent with its query.
    try {
      return { ...(crumb as object), message: undefined };
    } catch {
      return crumb;
    }
  }
};

const scrubCrumbData = (crumb: unknown): unknown => {
  try {
    const data: unknown = (crumb as { data?: unknown } | null)?.data;
    if (typeof data !== 'object' || data === null) return crumb;
    const { 'http.query': _query, ...rest } = data as Record<string, unknown>;
    for (const key of URL_KEYS) {
      if (typeof rest[key] === 'string') rest[key] = stripUrlQueries(rest[key]);
    }
    if (Array.isArray(rest.arguments)) rest.arguments = rest.arguments.map(stripIfString);
    return { ...(crumb as object), data: rest };
  } catch {
    // Fail closed: an unreadable data bag is dropped rather than sent with its query.
    try {
      return { ...(crumb as object), data: undefined };
    } catch {
      return crumb;
    }
  }
};

const scrubBreadcrumb = (crumb: unknown): unknown => scrubCrumbData(scrubCrumbMessage(crumb));

// Native http breadcrumbs carry the Facebook SDK `access_token` query. Only string values are
// touched and nothing is mutated in place, so frozen or circular payloads cannot abort the scrub.
const stripEventQueries = (event: QueryStrippableEvent): void => {
  bestEffort(() => {
    if (typeof event.message === 'string') event.message = stripUrlQueries(event.message);
  });
  bestEffort(() => {
    const args = event.extra?.arguments;
    if (event.extra && Array.isArray(args)) event.extra.arguments = args.map(stripIfString);
  });
  bestEffort(() => {
    const cause = event.extra?.cause;
    if (event.extra && typeof cause === 'object' && cause !== null && !Array.isArray(cause)) {
      const copy: Record<string, unknown> = { ...cause };
      for (const key of ['message', 'stack']) {
        if (typeof copy[key] === 'string') copy[key] = stripUrlQueries(copy[key]);
      }
      event.extra.cause = copy;
    }
  });
  bestEffort(() => {
    if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  });
};

class SentryError extends Error {
  constructor(
    message: string,
    public error: unknown,
    public context?: ReportErrorContext,
  ) {
    super(message);
    this.error = error;
    this.context = context;
  }
}

export const tagSentryInstallId = (installId: string): void => {
  Sentry.setUser({ id: installId });
  Sentry.setTag('install_id', installId);
};

const SENTRY_READY_TIMEOUT_MS = 5_000;

// @sentry/capacitor 4.x creates the JS client only after the native init round-trip resolves, so a
// capture in the same tick as initSentry() is silently dropped. Queue until the wrapped
// originalInit runs, or until the safety timer flushes.
let sentryReady = false;
let readyTimer: ReturnType<typeof setTimeout> | null = null;
const pendingCaptures: Array<() => void> = [];
// Capped: where initSentry never runs (dev builds, web) nothing flushes the queue.
const MAX_PENDING_CAPTURES = 50;

const flushPendingCaptures = (): void => {
  sentryReady = true;
  if (readyTimer) {
    clearTimeout(readyTimer);
    readyTimer = null;
  }
  for (const capture of pendingCaptures.splice(0)) {
    try {
      capture();
    } catch {
      // A capture must never take the caller down.
    }
  }
};

const captureWhenReady = (capture: () => void): void => {
  if (sentryReady || Sentry.getClient?.()) {
    capture();
    return;
  }
  if (pendingCaptures.length < MAX_PENDING_CAPTURES) pendingCaptures.push(capture);
};

// `persistInstallId: false` reads an existing install id but never creates one: `baobab_install_id`
// is a device identifier, and the web funnel of a consent country must not store it before the
// visitor accepts.
export const initSentry = ({
  persistInstallId = true,
}: { persistInstallId?: boolean } = {}) => {
  const isProduction = env.mode === 'production';
  // Armed before Sentry.init so a synchronous throw there still flushes.
  if (!sentryReady && !readyTimer) {
    readyTimer = setTimeout(flushPendingCaptures, SENTRY_READY_TIMEOUT_MS);
  }
  Sentry.init(
    {
      dsn: 'https://58a93481b17ea401d177a22918387a09@o4506868795768832.ingest.us.sentry.io/4506868798259200',
      release: getAppVersion(),
      integrations: [
        SentryReact.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        SentryReact.captureConsoleIntegration({
          levels: ['error'],
        }),
      ],
      ignoreErrors: [
        /Request failed with status code 40[014]/,
        'Failed to fetch dynamically imported module',
        // WebKit's wording for the same failure as 'Failed to fetch dynamically imported module'.
        // Without this, iOS chunk-load failures were never filtered.
        'Importing a module script failed',
        'Abort due to cancellation of share.',
        'AbortError: Share canceled',
        'Request timeout after',
      ],
      beforeSend(event, hint) {
        const consoleArg = Array.isArray(event.extra?.arguments)
          ? event.extra.arguments[0]
          : undefined;

        if (event.logger === 'console' && typeof consoleArg === 'string') {
          if (BENIGN_VENDOR_CONSOLE_ERRORS.includes(consoleArg)) {
            return null;
          }
          if (
            consoleArg.includes(CAPGO_SEMAPHORE_STEM) &&
            !consoleArg.trimEnd().endsWith(BENIGN_SEMAPHORE_SUFFIX)
          ) {
            // iOS-only vendor noise, NOT a bundle failure.
            // DO NOT DROP THIS EVENT: it is the acceptance signal for the plugin bump; the genuine
            // dead-bundle line ("notifyAppReady was not called") must keep arriving.
            event.fingerprint = ['capgo-ios-foreground-semaphore-noise'];
          }
          if (consoleArg === CONSOLE_SENTRY_LOG) {
            return null;
          }
          if (!event.fingerprint) {
            let verdict: CapgoLineVerdict | null = null;
            try {
              verdict = classifyCapgoLine(consoleArg);
            } catch {
              // a classifier defect must never lose the event
            }
            if (verdict) {
              if (verdict.drop) return null;
              event.fingerprint = verdict.fingerprint;
              event.tags = { ...event.tags, ...verdict.tags };
              event.extra = { ...verdict.extra, ...event.extra };
              event.message = verdict.message;
            }
          }
        }

        if (hint.originalException instanceof SentryError) {
          const { message, context } = hint.originalException;
          event.fingerprint = context?.fingerprint ?? ['{{ default }}', message];
          if (context?.tags) event.tags = { ...event.tags, ...context.tags };
          if (context?.extra) event.extra = { ...event.extra, ...context.extra };
        }

        try {
          stripEventQueries(event);
        } catch {
          // stripping is best-effort and must never lose the event
        }

        // Retroactive enrichment: the active bundle version is otherwise only knowable
        // asynchronously (CapacitorUpdater.current()), and the errors we most need to slice by
        // version fire *before* that resolves — which is why the early-boot init failures were
        // unsliceable by bundle. getAppVersion() resolves the same build-time version value every
        // launch of THIS bundle seeds at boot, so it is always the version of the bundle currently
        // executing — never the one it replaced, and never the builtin.
        if (!event.tags?.app_version) {
          event.tags = { ...event.tags, app_version: getAppVersion() };
        }

        return event;
      },
      // Transactions skip beforeSend but still carry the native http breadcrumbs.
      beforeSendTransaction(event) {
        try {
          stripEventQueries(event as QueryStrippableEvent);
        } catch {
          // stripping is best-effort and must never lose the event
        }
        return event;
      },
      // Runs synchronously at console time, so it sees plugin lines beforeSend later drops.
      beforeBreadcrumb(breadcrumb) {
        try {
          if (breadcrumb.category === 'console' && breadcrumb.level === 'error') {
            recordCapgoConsoleLine(breadcrumb.message);
          }
        } catch {
          // diagnostics must never break the console
        }
        return breadcrumb;
      },
      tracesSampleRate: isProduction ? 0.005 : 0.01,
      environment: env.mode,
    },
    (options) => {
      SentryReact.init(options);
      flushPendingCaptures();
    },
  );
  try {
    const installId = persistInstallId ? getOrCreateInstallId() : peekInstallId();
    if (installId) tagSentryInstallId(installId);
  } catch {
    // That native sync is exactly what throws when the bridge is unavailable: scopeSync's
    // setUser/setTag hooks call NATIVE.setUser/NATIVE.setTag, which `throw _NativeClientError`
    // unless `isPluginAvailable('SentryCapacitor')`.
  }

  (async () => {
    const version = (await CapacitorUpdater.current()).bundle.version || 'web';
    Sentry.setTag('app_version', version);
  })();
};

export const createBugReport = async (message: string) => {
  const currentBuild = await CapacitorUpdater.current();
  // Avoid a top-level Sentry <-> query base circular import.
  const { queryClient } = await import('src/queries/base');
  const user = queryClient.getQueryData<User>(queryKeys.user());
  reportError(`[BUG REPORT] ${message}`, {
    version: currentBuild.bundle.version,
    versionNative: currentBuild.native,
    platform: getPlatform(),
    isTestFlight: await isTestFlight(),
    userId: user?.id,
    familyId: user?.family_id,
    baseUrl: getBackendUrl(),
    environment: env.mode,
  });
};

// Capture before the console.error: console capture marks the Error as seen and drops a later capture.
export const reportErrorBoundary = (error: unknown, componentStack?: string | null) => {
  const tagged = error instanceof Error ? error : new Error(String(error));
  if (env.mode !== 'test') {
    captureWhenReady(() =>
      Sentry.captureException(error, buildErrorBoundaryContext(tagged, componentStack)),
    );
  }
  console.error(
    CONSOLE_SENTRY_LOG,
    'ErrorBoundary caught error',
    error,
    ...(componentStack ? [componentStack] : []),
  );
};

export const onReactCaughtError = (error: unknown, errorInfo: { componentStack?: string }) =>
  reportErrorBoundary(error, errorInfo.componentStack);

export const STUCK_FLUSH_TIMEOUT_MS = 2_000;

// A fresh Error: the boundary's own error is already marked captured and may match ignoreErrors.
export const reportUserStuck = async (
  error: unknown,
  signature: string,
  route: string,
): Promise<void> => {
  if (env.mode === 'test') return;
  try {
    const identity = errorIdentity(error);
    const stuckError = new Error(`User stuck on error screen at ${route}`);
    stuckError.name = 'UserStuckError';
    captureWhenReady(() =>
      Sentry.captureException(stuckError, {
        level: 'fatal',
        tags: { user_stuck: 'true', stuck_route: route, stuck_error: identity },
        fingerprint: ['user-stuck', signature],
        extra: { stuck_key: signature, error_identity: identity },
      }),
    );
    await Sentry.flush(STUCK_FLUSH_TIMEOUT_MS);
  } catch {
    // The reload that follows must never be blocked by a reporting failure.
  }
};

// Keys whose values must never reach Sentry, per CLAUDE.md's security non-negotiables.
const CREDENTIAL_KEY_PATTERN =
  /authorization|token|password|secret|api[_-]?key|cookie|jwt|private[_-]?key|signing[_-]?key|signature/i;

const MAX_CAUSE_DEPTH = 4;

// Placeholder for a value beyond MAX_CAUSE_DEPTH.
const DEPTH_CAP_PLACEHOLDER = '[depth-capped]';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  Object.getPrototypeOf(value) === Object.prototype;

const scrubCredentials = (value: unknown, depth = 0): unknown => {
  if (depth >= MAX_CAUSE_DEPTH) return DEPTH_CAP_PLACEHOLDER;
  if (Array.isArray(value)) {
    return value.map((entry) => scrubCredentials(entry, depth + 1));
  }
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) =>
      CREDENTIAL_KEY_PATTERN.test(key)
        ? [key, '[scrubbed]']
        : [key, scrubCredentials(entry, depth + 1)],
    ),
  );
};

export const buildErrorCause = (error: unknown): unknown => {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  return scrubCredentials(error);
};

export const reportError = (message: string, error?: unknown, context?: ReportErrorContext) => {
  let errorContext = error;
  if (axios.isAxiosError(error)) {
    errorContext = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
    };
  }

  // Passing a trailing `undefined` unconditionally (the pre-fix behaviour) leaks into the Sentry
  // message: captureConsoleIntegration finds no `Error` among a message-only call's console.error
  // args and falls back to joining all of them, rendering the literal text "undefined" onto the end
  // (e.g. bootSequence.ts's
  // `reportError('Reverting Capgo bundle: ...')`, which has no error to pass).
  if (errorContext !== undefined) {
    console.error(CONSOLE_SENTRY_LOG, message, errorContext);
  } else {
    console.error(CONSOLE_SENTRY_LOG, message);
  }
  if (env.mode !== 'test') {
    const cause = buildErrorCause(errorContext);
    captureWhenReady(() =>
      Sentry.captureException(new SentryError(message, errorContext, context), {
        extra: { cause },
      }),
    );
  }
};
